
import {
  ref, computed, defineComponent, onMounted, Ref,
} from '@vue/composition-api';
import Vue from 'vue';
import {
  Carousel, Slide, FCButton, FiveStars,
} from '@fc/angie-ui';
import FCImage from '~/components/FCImage.vue';
import SplitView from '~/components/SplitView.vue';
import SizeSelectors from '@/shared/components/SizeSelectors.vue';
import Video from '~/components/storyblok/Video/Video.vue';
import storeMutations from '~/store/constants/storeMutations';
import ProductsUpsell from '~/components/products/Products_Upsell.vue';
import ProductsIncludedItems from '~/components/products/Products_IncludedItems.vue';
import { IVideoExpectedAttrs } from '~/components/storyblok/Video/types';
import SmoothScroll from 'vue2-smooth-scroll';
import ptFightcampTrackers from '~/components/products/content/pt_fightcamp_trackers';

export interface ISelectedItem {
  itemId: string;
  product: {
    category?: string;
  }
}

export interface IPictureResponsive {
  _uid: string;
  Image: {
    filename: string;
    alt: string;
  }
}

const MEDIA_GRID = [
  {
    _uid: 'c02d5a5b-5910-4c21-acd8-ae505cdc1905',
    Image: {
      id: 8356188,
      alt: 'Real FightCamp customer in athletic wear and boxing gloves punches a white punching bag labeled "Fight Camp" in a home gym setting.',
      name: '',
      focus: '',
      title: '',
      filename: 'https://a.storyblok.com/f/152150/8256x6192/359f7a99cb/james-punch.jpg',
      copyright: '',
      fieldtype: 'asset',
      is_external_url: false,
    },
    codeId: '',
    component: 'PictureResponsive',
    imageType: '',
    _editable: '<!--#storyblok#{"name": "PictureResponsive", "space": "152150", "uid": "c02d5a5b-5910-4c21-acd8-ae505cdc1905", "id": "272363633"}-->',
  },
  {
    _uid: 'c02d5a5b-5910-4c21-acd8-ae505cdc1905',
    Image: {
      id: 8356188,
      alt: 'Woman punching FightCamp bag while watching Coach Jess on her TV. The FightCamp console is on the entertainment center.',
      name: '',
      focus: '',
      title: '',
      filename: 'https://a.storyblok.com/f/152150/1200x1200/7710ffa1d2/largesquare.jpg',
      copyright: '',
      fieldtype: 'asset',
      is_external_url: false,
    },
    codeId: '',
    component: 'PictureResponsive',
    imageType: '',
    _editable: '<!--#storyblok#{"name": "PictureResponsive", "space": "152150", "uid": "c02d5a5b-5910-4c21-acd8-ae505cdc1905", "id": "272363633"}-->',
  },
  {
    _uid: 'bd40370e-2cfa-4af7-b7b1-dd659762a1ad',
    Image: {
      id: 6615565,
      alt: 'Woman wearing FightCamp gloves punching FightCamp bag with her left arm in her living room. A FightCamp workout is on the TV led by Coach Jess.',
      name: '',
      focus: '',
      title: '',
      source: '',
      filename: 'https://a.storyblok.com/f/152150/1200x1200/48eaef6d23/largesquare2.jpg',
      copyright: '',
      fieldtype: 'asset',
      meta_data: {},
      is_private: '',
      is_external_url: false,
    },
    codeId: '',
    component: 'PictureResponsive',
    imageType: '',
    _editable: '<!--#storyblok#{"name": "PictureResponsive", "space": "152150", "uid": "bd40370e-2cfa-4af7-b7b1-dd659762a1ad", "id": "272363633"}-->',
  },
  {
    _uid: 'b475d972-bf0f-4ee8-9887-83ecae3f72d3',
    Image: {
      id: 8113166,
      alt: 'FightCamp Trackers and Quick Wraps',
      name: '',
      focus: '',
      title: '',
      filename: 'https://a.storyblok.com/f/152150/964x883/151cdb7a76/fc-trackers-product-atf-wraps-001.png',
      copyright: '',
      fieldtype: 'asset',
      is_external_url: false,
    },
    codeId: '',
    component: 'PictureResponsive',
    imageType: '',
    _editable: '<!--#storyblok#{"name": "PictureResponsive", "space": "152150", "uid": "b475d972-bf0f-4ee8-9887-83ecae3f72d3", "id": "272363633"}-->',
  },
];
const prod = {
  title: ptFightcampTrackers?.title || '',
  text: ptFightcampTrackers?.text || '',
  description: ptFightcampTrackers?.description || '',
  price: ptFightcampTrackers?.product.price || 0,
  oldPrice: ptFightcampTrackers?.product.oldPrice || 0,
  financing: {
    price: ptFightcampTrackers?.product.monthly || 0,
    months: ptFightcampTrackers?.product.months || 0,
  },
  includes: ptFightcampTrackers?.includes || [],
  upsell: ptFightcampTrackers?.upsell || [],
  id: ptFightcampTrackers?.id || '',
  selects: ptFightcampTrackers?.selects || [],
  variants: ptFightcampTrackers?.variants || [],
  bonusOptions: ptFightcampTrackers?.bonusOptions || [],
};

Vue.use(SmoothScroll, {
  duration: 500,
  updateHistory: true,
});

export default defineComponent({
  name: 'PTAboveTheFold',
  components: {
    SplitView,
    FCImage,
    Video,
    FiveStars,
    ProductsIncludedItems,
    SizeSelectors,
    ProductsUpsell,
    FCButton,
    Carousel,
    Slide,
  },
  setup() {
    const {
      $store,
      $smoothScroll,
    // @ts-ignore
    } = useNuxtApp();

    const mediaGrid = MEDIA_GRID;
    function isImage(media: IVideoExpectedAttrs | IPictureResponsive): media is IPictureResponsive {
      return (media as IPictureResponsive).Image !== undefined;
    }

    function getImageSrc(media: IPictureResponsive | IVideoExpectedAttrs) {
      if (isImage(media)) {
        return media.Image.filename;
      }
      return '';
    }

    function getImageAlt(media: IPictureResponsive | IVideoExpectedAttrs) {
      if (isImage(media)) {
        return media.Image.alt;
      }
      return '';
    }

    function scrollToReviews() {
      $smoothScroll({
        scrollTo: document.getElementById('customerReviews'),
        offset: -100,
        updateHistory: false,
      });
    }

    const selectedSize = ref();
    const updateSizeValue = (value: 's' | 'l') => {
      selectedSize.value = value;
    };
    const total = ref(0);
    const formattedTotal = computed(() => {
      const value = total.value / 100;
      return value ? value.toFixed(2) : '';
    });
    const selectedItems: Ref<ISelectedItem[]> = ref([]);
    const loading = ref(false);
    function findVariant() {
      return prod.variants?.find((v) => {
        // eslint-disable-next-line guard-for-in
        for (const key in v.keys) {
          const selected = selectedSize.value;
          if (selected !== v.keys[key]) {
            return false;
          }
        }
        return true;
      });
    }

    function handleTotal($event: number) {
      total.value = $event;
      $store.commit(storeMutations.SET_PACKAGE_PAGE_TOTAL, $event);
    }

    onMounted(() => {
      $store.commit(storeMutations.SET_PACKAGE_PAGE_TOTAL, prod?.price);
      total.value = prod?.price || 0;
    });

    async function addToCart() {
      loading.value = true;
      const variant = findVariant();

      // clear items to handle if shopify items are in cart
      await $store.dispatch('clearCart');

      await $store.dispatch('insertItemToCart', {
        id: variant?.sku,
        type: 'equip',
        disableDrawer: true,
      });

      selectedItems.value.forEach(async (item) => {
        await $store.dispatch('insertItemToCart', {
          id: item.itemId,
          type: item.product.category === 'warranty' ? 'warranty' : 'equip',
        });
      });

      $store.commit('SET_MEMBERSHIP_TYPE', {
        key: 'Partner_FightCamp_1_Month',
        name: 'Monthly',
        type: 'partnerfightcampplan',
      });

      $store.commit('closeDrawer');
      window.$nuxt.$router.push('/shop/partner/checkout');
      loading.value = false;
    }

    const displayText = computed(() => {
      if (process.client) {
        return !new URLSearchParams(window.location.search).has('coupon');
      }
      return true;
    });

    return {
      mediaGrid,
      isImage,
      getImageSrc,
      getImageAlt,
      prod,
      scrollToReviews,
      updateSizeValue,
      handleTotal,
      addToCart,
      formattedTotal,
      loading,
      selectedItems,
      displayText,
    };
  },
});
